import React, { useCallback, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetEntitiesQuery } from '../../../store/integrations/integrations'
import { addToast } from '../../../store/toast/toastSlice'
import { IntegrationServiceId } from '../../../common/integrations'
import { SelectDropdown, SelectOption } from '../../SelectDropdown/SelectDropdown'
import { IntegrationFieldValue } from './IntegrationFieldReview'
import { useResolvedReferenceLabel } from './useResolvedReferenceLabel'

const SEARCH_RESULTS_LIMIT = 25

export interface IntegrationReferenceFieldSelectProps {
  serviceId: IntegrationServiceId
  referenceObjectName?: string
  value: IntegrationFieldValue
  onSelect: (id: string) => void
  buildEntityListRoutingKey: (referenceObjectName: string) => string
  buildEntitySingleRoutingKey: (referenceObjectName: string) => string
}

export const IntegrationReferenceFieldSelect: React.FC<IntegrationReferenceFieldSelectProps> = ({
  serviceId,
  referenceObjectName,
  value,
  onSelect,
  buildEntityListRoutingKey,
  buildEntitySingleRoutingKey,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [options, setOptions] = useState<SelectOption[]>([])
  const [triggerGetRecords] = useLazyGetEntitiesQuery()

  const displayValue = value !== undefined && value !== null && value !== '' ? String(value) : ''
  const { label: resolvedLabel, isLoading } = useResolvedReferenceLabel(serviceId, referenceObjectName, displayValue, buildEntitySingleRoutingKey)

  const initialSelectedOption = useMemo<SelectOption | null>(
    () => (displayValue && !isLoading ? { id: displayValue, label: resolvedLabel ?? displayValue } : null),
    [displayValue, isLoading, resolvedLabel],
  )

  const handleSearch = useCallback(
    async (input: string) => {
      if (!referenceObjectName) {
        return
      }
      try {
        const result = await triggerGetRecords({
          accountId,
          connector: serviceId,
          entityType: buildEntityListRoutingKey(referenceObjectName),
          search: input,
          limit: SEARCH_RESULTS_LIMIT,
        }).unwrap()
        const items = result.items ?? []
        setOptions(items.map((item) => ({ id: item.id, label: item.referenceValues[0]?.value || item.id })))
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Search failed', messages: ['Please try again.'] }))
      }
    },
    [accountId, referenceObjectName, serviceId, triggerGetRecords, buildEntityListRoutingKey, dispatch],
  )

  if (!referenceObjectName) {
    return <Form.Control disabled placeholder='Reference target unknown' />
  }

  return (
    <SelectDropdown
      placeholder='Search…'
      options={options}
      initialSelectedOption={initialSelectedOption}
      searchable
      loading={isLoading}
      onSearchInputChange={handleSearch}
      onOptionSelected={(o) => onSelect(o?.id ?? '')}
      className='w-100'
    />
  )
}
